'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, ChainId } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { Timelock } from '@repo/ui/icons';
import { memo } from 'react';
import { PendingActionsList, VaultPendingTimelockInfo } from '@/components/VaultPage/components/VaultPendingTimelockInfo';
import { getTimelockFunctionConfig as getV1TimelockFunctionConfig } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/utils';
import type { UnderlyingVaultInfo } from '../../../types/pendingTimelocks';
import type { PendingTimelockItem, UnderlyingVaultPendingTimelock, VaultV2Caps, VaultV2PendingTimelock as VaultV2PendingTimelockType } from '../../../types/pendingTimelocks';
import { VaultV2PendingTimelock } from '../../../VaultV2PendingTimelock';
import UnderlyingVaultPendingTimelockItem from '../../../VaultV2PendingTimelock/UnderlyingVaultPendingTimelockItem';
import { getTimelockCriticality } from '../../../VaultV2PendingTimelock/utils';
interface VaultV2ExposurePendingTimelocksIndicatorProps {
  chainId: ChainId;
  pendingTimelocks: VaultV2PendingTimelockType[];
  underlyingVaultPendingTimelocks?: UnderlyingVaultPendingTimelock[];
  underlyingVaultMarketSupplyCaps?: Record<string, BigIntish>;
  underlyingVault?: UnderlyingVaultInfo;
  caps: VaultV2Caps;
  vaultSupplyToken?: InterfaceToken | null;
}
const VaultV2ExposurePendingTimelocksIndicator = t0 => {
  const $ = _c(23);
  const {
    chainId,
    pendingTimelocks,
    underlyingVaultPendingTimelocks,
    underlyingVaultMarketSupplyCaps,
    underlyingVault,
    caps,
    vaultSupplyToken
  } = t0;
  let items;
  let t1;
  if ($[0] !== pendingTimelocks || $[1] !== underlyingVaultMarketSupplyCaps || $[2] !== underlyingVaultPendingTimelocks) {
    items = [...pendingTimelocks.map(_temp), ...(underlyingVaultPendingTimelocks ?? []).map(_temp2)];
    t1 = items.some(entry => {
      if (entry.source === "v2") {
        return getTimelockCriticality(entry.item.functionName) === "high";
      }
      const hasPreviousValue = entry.item.decodedData?.__typename === "VaultSetCapPendingData" && entry.item.decodedData.market && underlyingVaultMarketSupplyCaps ? isDefined(underlyingVaultMarketSupplyCaps[entry.item.decodedData.market.marketId]) : undefined;
      return getV1TimelockFunctionConfig({
        functionName: entry.item.functionName,
        hasPreviousValue
      })?.criticality === "high";
    });
    $[0] = pendingTimelocks;
    $[1] = underlyingVaultMarketSupplyCaps;
    $[2] = underlyingVaultPendingTimelocks;
    $[3] = items;
    $[4] = t1;
  } else {
    items = $[3];
    t1 = $[4];
  }
  const hasCriticalAction = t1;
  const iconColorClass = hasCriticalAction ? "text-destructive-foreground" : "text-info-foreground";
  const t2 = items.length;
  let t3;
  if ($[5] !== caps || $[6] !== chainId || $[7] !== items || $[8] !== underlyingVault || $[9] !== underlyingVaultMarketSupplyCaps || $[10] !== vaultSupplyToken) {
    t3 = items.map((entry_0, index) => {
      if (entry_0.source === "v2") {
        return <VaultV2PendingTimelock key={`v2-${entry_0.item.functionName}-${index}`} validAt={entry_0.item.validAt} functionName={entry_0.item.functionName} decodedData={entry_0.item.decodedData} disableNavigation={true} caps={caps ?? undefined} vaultSupplyToken={vaultSupplyToken} />;
      }
      return <UnderlyingVaultPendingTimelockItem key={`v1-${entry_0.item.functionName}-${index}`} chainId={chainId} pendingTimelock={entry_0.item} marketSupplyCaps={underlyingVaultMarketSupplyCaps} vault={underlyingVault} />;
    });
    $[5] = caps;
    $[6] = chainId;
    $[7] = items;
    $[8] = underlyingVault;
    $[9] = underlyingVaultMarketSupplyCaps;
    $[10] = vaultSupplyToken;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== items.length || $[13] !== t3) {
    t4 = <PendingActionsList count={t2}>{t3}</PendingActionsList>;
    $[12] = items.length;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const pendingActionsContent = t4;
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Timelock />;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== iconColorClass) {
    t6 = cn("size-5", iconColorClass);
    $[16] = iconColorClass;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <Icon icon={t5} className={t6} />;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== pendingActionsContent || $[21] !== t7) {
    t8 = <VaultPendingTimelockInfo side="top" content={pendingActionsContent}>{t7}</VaultPendingTimelockInfo>;
    $[20] = pendingActionsContent;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
export default memo(VaultV2ExposurePendingTimelocksIndicator);
function _temp(item) {
  return {
    source: "v2" as const,
    item
  };
}
function _temp2(item_0) {
  return {
    source: "v1" as const,
    item: item_0
  };
}